import { AlertCircle, Archive, CheckCircle, Clock, CreditCard, Eye, FileText, Send, UserCheck } from 'lucide-react';

export const statusConfig = {
  order_received: { label: 'Order Received', icon: CheckCircle, bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
  payment_pending: { label: 'Payment Pending', icon: CreditCard, bgColor: 'bg-amber-100', textColor: 'text-amber-700' },
  awaiting_payment: { label: 'Awaiting Payment', icon: CreditCard, bgColor: 'bg-amber-100', textColor: 'text-amber-700' },
  awaiting_credit_approval: { label: 'Credit Review', icon: Clock, bgColor: 'bg-orange-100', textColor: 'text-orange-700' },
  in_preparation: { label: 'In Preparation', icon: Clock, bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
  being_tagged: { label: 'Being Tagged', icon: FileText, bgColor: 'bg-muted', textColor: 'text-foreground' },
  quality_review: { label: 'Quality Review', icon: UserCheck, bgColor: 'bg-orange-100', textColor: 'text-orange-700' },
  ready_for_review: { label: 'Ready for Your Review', icon: Eye, bgColor: 'bg-green-100', textColor: 'text-green-700' },
  filing_in_progress: { label: 'Filing in Progress', icon: Send, bgColor: 'bg-muted', textColor: 'text-foreground' },
  filed: { label: 'Filed', icon: CheckCircle, bgColor: 'bg-teal-100', textColor: 'text-teal-700' },
  completed: { label: 'Completed', icon: CheckCircle, bgColor: 'bg-green-100', textColor: 'text-green-700' },
  archived: { label: 'Archived', icon: Archive, bgColor: 'bg-gray-100', textColor: 'text-gray-500' },
  on_hold: { label: 'On Hold', icon: AlertCircle, bgColor: 'bg-amber-100', textColor: 'text-amber-700' },
  action_needed: { label: 'Action Needed', icon: AlertCircle, bgColor: 'bg-red-100', textColor: 'text-red-700' },
  cancelled: { label: 'Cancelled', icon: Archive, bgColor: 'bg-gray-100', textColor: 'text-gray-500' },
};

export const LEGACY_STATUS_ALIASES = {
  file_pending: 'order_received',
  file_uploaded: 'order_received',
  pending: 'order_received',
  awaiting_expert: 'in_preparation',
  waiting_expert_review: 'in_preparation',
  processing: 'in_preparation',
  waiting_customer_review: 'ready_for_review',
};

export const CLIENT_STATUS_GROUPS = {
  active: ['order_received', 'in_preparation', 'being_tagged', 'quality_review', 'filing_in_progress'],
  review: ['ready_for_review', 'action_needed'],
  completed: ['completed', 'filed', 'archived'],
  payment_pending: ['payment_pending'],
  on_hold: ['on_hold'],
};

export function normalizeClientStatus(status) {
  if (!status) return 'order_received';
  return LEGACY_STATUS_ALIASES[status] || status;
}

function humanizeStatus(status) {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

export function getStatusLabel(status) {
  const normalized = normalizeClientStatus(status);
  return statusConfig[normalized]?.label || humanizeStatus(normalized);
}

export function isReviewStatus(status) {
  return normalizeClientStatus(status) === 'ready_for_review';
}

export function getStatusBadge(status) {
  const normalized = normalizeClientStatus(status);
  const config = statusConfig[normalized] || statusConfig.order_received;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

// ── Progress Stepper ──────────────────────────────────────
// 7 client-visible steps, each mapping to one or more raw status values.

export const STATUS_STEPS = [
  {
    key: 'order_received',
    label: 'Order Placed',
    clientStatuses: ['order_received', 'payment_pending', 'awaiting_payment', 'awaiting_credit_approval'],
  },
  {
    key: 'in_preparation',
    label: 'In Preparation',
    clientStatuses: ['in_preparation', 'being_tagged'],
  },
  {
    key: 'quality_review',
    label: 'Quality Review',
    clientStatuses: ['quality_review'],
  },
  {
    key: 'ready_for_review',
    label: 'Your Review',
    clientStatuses: ['ready_for_review'],
  },
  {
    key: 'filing_in_progress',
    label: 'Filing',
    clientStatuses: ['filing_in_progress'],
  },
  {
    key: 'filed',
    label: 'Filed',
    clientStatuses: ['filed'],
  },
  {
    key: 'completed',
    label: 'Completed',
    clientStatuses: ['completed', 'archived'],
  },
];

/**
 * Returns the 0-based step index for a given client status.
 * Returns 0 for unknown statuses (degrades gracefully).
 */
export function getStepIndex(clientStatus) {
  const normalized = normalizeClientStatus(clientStatus);
  const idx = STATUS_STEPS.findIndex(s => s.clientStatuses.includes(normalized));
  return idx === -1 ? 0 : idx;
}
